import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Check, X as XIcon, Calculator, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiClient, extractErrorMessage } from '@/lib/api';

interface Row {
  id: string;
  driverName: string | null;
  driverCode: string | null;
  vehiclePlate: string | null;
  shiftDate: string;
  tripsCount: number;
  tripsRevenueSar: number;
  expectedCashSar: number | null;
  actualCashHandedSar: number | null;
  varianceSar: number | null;
  status: string;
  submittedAt: string | null;
  notes: string | null;
}

const fmtSar = (n: number) => 'SAR ' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function ClosingsAdminPage() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get<Row[]>('/api/v1/daily-closings/pending');
      setRows(r.data);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  async function reconcile(id: string) {
    setBusyId(id);
    try {
      await apiClient.post('/api/v1/daily-closings/' + id + '/reconcile');
      await refresh();
    } catch (err) { window.alert(extractErrorMessage(err)); }
    finally { setBusyId(null); }
  }

  async function dispute(id: string) {
    const reason = window.prompt('Reason for dispute?');
    if (!reason) return;
    setBusyId(id);
    try {
      await apiClient.post('/api/v1/daily-closings/' + id + '/dispute', { reason });
      await refresh();
    } catch (err) { window.alert(extractErrorMessage(err)); }
    finally { setBusyId(null); }
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Link to="/dashboard">
            <Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Dashboard</Button>
          </Link>
          <div className="text-sm font-medium inline-flex items-center gap-2"><Calculator className="h-4 w-4" /> Closings — pending review</div>
          <div className="w-24" />
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-4 space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : error ? (
          <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm inline-flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> {error}</div>
        ) : rows.length === 0 ? (
          <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">No closings pending review.</div>
        ) : (
          <div className="space-y-2">
            {rows.map((r) => {
              const flagged = r.varianceSar !== null && Math.abs(r.varianceSar) > 50;
              return (
                <div key={r.id} className={'rounded-lg border p-3 ' + (flagged ? 'border-rose-500/40' : 'border-border')}>
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="font-medium text-sm">{r.driverName ?? r.driverCode ?? '—'}</div>
                    <div className="text-xs text-muted-foreground font-mono">{r.shiftDate} {r.vehiclePlate ? '· ' + r.vehiclePlate : ''}</div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 text-xs">
                    <div><div className="text-muted-foreground">Trips</div><div className="font-mono">{r.tripsCount}</div></div>
                    <div><div className="text-muted-foreground">Expected</div><div className="font-mono">{r.expectedCashSar !== null ? fmtSar(r.expectedCashSar) : '—'}</div></div>
                    <div><div className="text-muted-foreground">Actual</div><div className="font-mono">{r.actualCashHandedSar !== null ? fmtSar(r.actualCashHandedSar) : '—'}</div></div>
                    <div><div className="text-muted-foreground">Variance</div><div className={'font-mono ' + (flagged ? 'text-rose-600 dark:text-rose-400' : '')}>{r.varianceSar !== null ? (r.varianceSar > 0 ? '+' : '') + fmtSar(r.varianceSar) : '—'}</div></div>
                  </div>
                  {r.notes && <div className="text-xs mt-2 text-muted-foreground whitespace-pre-wrap">{r.notes}</div>}
                  <div className="flex gap-2 mt-3 justify-end">
                    <Button size="sm" variant="outline" disabled={busyId === r.id} onClick={() => dispute(r.id)} className="gap-1.5"><XIcon className="h-3.5 w-3.5" /> Dispute</Button>
                    <Button size="sm" disabled={busyId === r.id} onClick={() => reconcile(r.id)} className="gap-1.5">{busyId === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Reconcile</Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
